import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Truck, Users, FileCheck, Calendar, AlertTriangle, TrendingUp, Activity } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const KPIPhase1 = () => {
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchKPI();
  }, []);

  const fetchKPI = async () => {
    try {
      setLoading(true);
      const response = await api.get('/kpi/phase1');
      setKpi(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des KPI');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!kpi) {
    return <div className="text-center py-8">Aucune donnée disponible</div>;
  }

  const { utilisation_flotte, conformite_documents, age_flotte, disponibilite_chauffeurs, alertes } = kpi;

  const getStatusColor = (value, thresholds = { good: 80, warning: 60 }) => {
    if (value >= thresholds.good) return 'text-green-600 bg-green-50';
    if (value >= thresholds.warning) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">KPI Opérationnels - Phase 1</h1>
          <p className="text-gray-600">Indicateurs de performance clés avec données existantes</p>
        </div>
        <button
          onClick={fetchKPI}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
        >
          <Activity className="h-4 w-4" />
          Actualiser
        </button>
      </div>

      {/* Alertes Globales */}
      {(alertes.vehicules_inactifs_30j > 0 || alertes.documents_expiration_proche_60j > 0 || alertes.vehicules_anciens_plus_10ans > 0) && (
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-900">Alertes Importantes</h3>
                <ul className="mt-2 space-y-1 text-amber-800">
                  {alertes.vehicules_inactifs_30j > 0 && (
                    <li>• {alertes.vehicules_inactifs_30j} véhicules inactifs depuis 30 jours</li>
                  )}
                  {alertes.documents_expiration_proche_60j > 0 && (
                    <li>• {alertes.documents_expiration_proche_60j} documents expirent dans 60 jours</li>
                  )}
                  {alertes.vehicules_anciens_plus_10ans > 0 && (
                    <li>• {alertes.vehicules_anciens_plus_10ans} véhicules ont plus de 10 ans</li>
                  )}
                  {alertes.chauffeurs_inactifs > 0 && (
                    <li>• {alertes.chauffeurs_inactifs} chauffeurs inactifs ou suspendus</li>
                  )}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* 1. Taux Utilisation Flotte */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <Truck className="h-8 w-8 text-blue-600" />
              <div className={`px-3 py-1 rounded-full font-semibold ${getStatusColor(utilisation_flotte.taux_utilisation_30j)}`}>
                {utilisation_flotte.taux_utilisation_30j}%
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <h3 className="font-bold text-lg">Utilisation Flotte</h3>
            <p className="text-gray-600 text-sm mb-3">30 derniers jours</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Total véhicules:</span>
                <span className="font-semibold">{utilisation_flotte.total_vehicules}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Actifs (30j):</span>
                <span className="font-semibold text-green-600">{utilisation_flotte.vehicules_actifs_30j}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Inactifs (30j):</span>
                <span className="font-semibold text-red-600">{utilisation_flotte.vehicules_inactifs_30j}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-gray-600">Moy. courses/véhicule:</span>
                <span className="font-semibold">{utilisation_flotte.moyenne_courses_par_vehicule_30j}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 2. Conformité Documents */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <FileCheck className="h-8 w-8 text-purple-600" />
              <div className={`px-3 py-1 rounded-full font-semibold ${getStatusColor(conformite_documents.taux_conformite_global)}`}>
                {conformite_documents.taux_conformite_global}%
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <h3 className="font-bold text-lg">Conformité Docs</h3>
            <p className="text-gray-600 text-sm mb-3">Documents à jour</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Permis chauffeurs:</span>
                <span className="font-semibold">{conformite_documents.chauffeurs.taux_conformite}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Cartes grises:</span>
                <span className="font-semibold">{conformite_documents.vehicules.taux_conformite_carte_grise}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Assurances:</span>
                <span className="font-semibold">{conformite_documents.vehicules.taux_conformite_assurance}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Contrôles tech:</span>
                <span className="font-semibold">{conformite_documents.vehicules.taux_conformite_controle}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 3. Âge Moyen Flotte */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <Calendar className="h-8 w-8 text-orange-600" />
              <div className="px-3 py-1 rounded-full font-semibold bg-blue-50 text-blue-600">
                {age_flotte.age_moyen_annees} ans
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <h3 className="font-bold text-lg">Âge de la Flotte</h3>
            <p className="text-gray-600 text-sm mb-3">Répartition par âge</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">0-2 ans:</span>
                <span className="font-semibold text-green-600">{age_flotte.vehicules_par_tranche["0-2_ans"]}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">3-5 ans:</span>
                <span className="font-semibold text-blue-600">{age_flotte.vehicules_par_tranche["3-5_ans"]}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">6-10 ans:</span>
                <span className="font-semibold text-yellow-600">{age_flotte.vehicules_par_tranche["6-10_ans"]}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">+10 ans:</span>
                <span className="font-semibold text-red-600">{age_flotte.vehicules_par_tranche.plus_10_ans}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 4. Disponibilité Chauffeurs */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <Users className="h-8 w-8 text-green-600" />
              <div className={`px-3 py-1 rounded-full font-semibold ${getStatusColor(disponibilite_chauffeurs.taux_disponibilite)}`}>
                {disponibilite_chauffeurs.taux_disponibilite}%
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <h3 className="font-bold text-lg">Disponibilité</h3>
            <p className="text-gray-600 text-sm mb-3">Chauffeurs actifs</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Total chauffeurs:</span>
                <span className="font-semibold">{disponibilite_chauffeurs.total_chauffeurs}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Actifs:</span>
                <span className="font-semibold text-green-600">{disponibilite_chauffeurs.chauffeurs_actifs}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">En mission:</span>
                <span className="font-semibold text-blue-600">{disponibilite_chauffeurs.chauffeurs_en_mission}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Disponibles:</span>
                <span className="font-semibold text-green-600">{disponibilite_chauffeurs.chauffeurs_disponibles_estimes}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Détails Supplémentaires */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Utilisation Détaillée */}
        <Card>
          <CardHeader>
            <CardTitle>Détails Utilisation Flotte</CardTitle>
            <CardDescription>Analyse sur 7 et 30 jours</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Actifs 7 derniers jours</p>
                  <p className="text-2xl font-bold text-blue-600">{utilisation_flotte.vehicules_actifs_7j}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-blue-600" />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Actifs 30 derniers jours</p>
                  <p className="text-2xl font-bold text-emerald-600">{utilisation_flotte.vehicules_actifs_30j}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-emerald-600" />
              </div>

              <div className="pt-4 border-t">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Taux utilisation 7j:</span>
                  <span className="text-lg font-bold">{utilisation_flotte.taux_utilisation_7j}%</span>
                </div>
                <div className="mt-2 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${utilisation_flotte.taux_utilisation_7j}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Conformité Détaillée */}
        <Card>
          <CardHeader>
            <CardTitle>Alertes Documents</CardTitle>
            <CardDescription>Documents expirant dans 60 jours</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {conformite_documents.chauffeurs.permis_expiration_proche_60j > 0 && (
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <span className="text-amber-800">Permis chauffeurs</span>
                  <span className="font-bold text-amber-600">{conformite_documents.chauffeurs.permis_expiration_proche_60j}</span>
                </div>
              )}
              
              {conformite_documents.vehicules.cartes_grises_expiration_proche_60j > 0 && (
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <span className="text-amber-800">Cartes grises</span>
                  <span className="font-bold text-amber-600">{conformite_documents.vehicules.cartes_grises_expiration_proche_60j}</span>
                </div>
              )}
              
              {conformite_documents.vehicules.assurances_expiration_proche_60j > 0 && (
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <span className="text-amber-800">Assurances</span>
                  <span className="font-bold text-amber-600">{conformite_documents.vehicules.assurances_expiration_proche_60j}</span>
                </div>
              )}
              
              {conformite_documents.vehicules.controles_expiration_proche_60j > 0 && (
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <span className="text-amber-800">Contrôles techniques</span>
                  <span className="font-bold text-amber-600">{conformite_documents.vehicules.controles_expiration_proche_60j}</span>
                </div>
              )}

              {alertes.documents_expiration_proche_60j === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <FileCheck className="h-12 w-12 mx-auto mb-2 text-green-500" />
                  <p>Aucun document n'expire dans les 60 prochains jours</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default KPIPhase1;
